import { Link } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon, GiftIcon } from 'lucide-react';
import { ProductImage } from '../../../ui/ProductImage';
import { useCatalog } from '../../../contexts/CatalogContext';
import { useSignedInAccount } from '../../../contexts/AccountContext';
import { founder } from '../../../data/content';
import { plural } from '../../../core/format/text';
import { dayLong } from '../../../utils/account';
import { giftCardCheck, isAwaitingDelivery, isGiftCardOnly, orderHref, ordersToFollow } from '../../../utils/accountOrders';
import type { AccountOrder } from '../../../core/types/account';
import type { Product } from '../../../core/types/catalog';
import { OrderTracking } from '../OrderTracking';

/** Zoveel productfoto's naast elkaar; de rest wordt "+2". */
const THUMBS = 3;
const steps: {
  status: AccountOrder['status'];
  label: string;
}[] = [{
  status: 'In behandeling',
  label: 'Besteld'
}, {
  status: 'Onderweg',
  label: 'Onderweg'
}, {
  status: 'Bezorgd',
  label: 'Bezorgd'
}];
const headline: Record<AccountOrder['status'], string> = {
  'In behandeling': `${founder.firstName} pakt je bestelling in`,
  Onderweg: 'Je pakket is onderweg',
  Bezorgd: 'Je pakket is bezorgd'
};

/**
 * De bestelling die ze nu volgt, bovenaan Mijn omgeving: voor elke klant, met of zonder
 * abonnement. Wat ze wil weten (waar is mijn pakket?) wint van alles eronder.
 * Niets te volgen: geen kaart.
 */
export function OrderStatusCard() {
  const {
    account
  } = useSignedInAccount();
  const {
    productById
  } = useCatalog();
  const isGift = giftCardCheck(productById);
  const list = ordersToFollow(account.orders, isGift);
  // Een pakket dat nog komt gaat voor een net bezorgde bestelling of een cadeaubon per mail.
  const order = list.find(o => isAwaitingDelivery(o, isGift)) ?? list[0];
  const others = list.filter(o => o !== order);
  if (!order) return null;
  const giftOnly = isGiftCardOnly(order, isGift);
  const current = steps.findIndex(s => s.status === order.status);
  const products = order.lines.map(l => productById(l.productId)).filter((p): p is Product => Boolean(p));
  const moreOpen = others.filter(o => isAwaitingDelivery(o, isGift)).length;
  const title = giftOnly ? 'Je cadeaubon staat in je mail' : headline[order.status];
  return <section aria-labelledby="order-status-title" className="bg-shell ring-1 ring-line" data-id="element-6063" data-mp="ht044r4z89fu">
      <div className="flex flex-col gap-5 px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-7 sm:py-6" data-id="element-6064" data-mp="hvdvaowjd1i4">
        <div className="min-w-0" data-id="element-6065" data-mp="83gkhj9fwbjx">
          <p className="text-[13px] text-muted" data-id="element-6066" data-mp="825d79gsclo2">
            Je bestelling van <span className="whitespace-nowrap" data-id="element-6067" data-mp="41d4chbtsisb">{dayLong(order.date)}</span>
          </p>
          <h2 id="order-status-title" className="mt-1 font-display text-[24px] font-light leading-tight text-ink sm:text-[28px]" data-id="element-6068" data-mp="3v7wh42182it9">
            {title}
          </h2>
          {giftOnly && <p className="mt-2 max-w-md text-[14px] leading-relaxed text-muted" data-id="element-6069" data-mp="3l9qdnfzrp4ma">
              Er gaat niets op de post. De bon met zijn code staat in je bevestigingsmail; print hem of stuur hem door.
            </p>}
        </div>
        {products.length > 0 && <ul className="flex shrink-0 items-center gap-2" aria-label="In deze bestelling" data-id="element-6070" data-mp="3g31stpi8bpqw">
            {products.slice(0, THUMBS).map(p => <li key={p.id} className="h-14 w-14 bg-sand" title={p.name} data-id="element-6071" data-mp="3q3lv8vhhj0ap">
                <ProductImage src={p.image} alt={p.name} className="h-full w-full p-1" data-id="element-6072" data-mp="2sfbt8ghp8cjl" />
              </li>)}
            {products.length > THUMBS && <li className="flex h-14 w-14 items-center justify-center bg-sand text-[13px] text-muted" data-id="element-6073" data-mp="2xeodqul3l7j0">
                <span className="sr-only" data-id="element-6074" data-mp="32c6pstuew2v8">en nog </span>+{products.length - THUMBS}
              </li>}
          </ul>}
      </div>

      {giftOnly ? <div className="flex items-center gap-3 border-t border-line px-5 py-3.5 text-[14px] text-muted sm:px-7" data-id="element-6075" data-mp="29nr9m3zjneib">
          <GiftIcon className="h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-6076" data-mp="2emcewhtnkodm" />
          Niet gekregen? Kijk in je spam, of vraag {founder.firstName} hem opnieuw te sturen.
        </div> : <>
          <ol className="grid grid-cols-3 gap-2 px-5 pb-5 sm:px-7" aria-label="Status" data-id="element-6077" data-mp="f6rawp9mkg3j">
            {steps.map((s, i) => {
          const done = i <= current;
          return <li key={s.status} aria-current={i === current ? 'step' : undefined} className="min-w-0" data-id="element-6078" data-mp="p4p0bg380ixq" data-mpi={i}>
                  <span className={`block h-1 rounded-full ${done ? 'bg-clay' : 'bg-line'}`} aria-hidden="true" data-id="element-6079" data-mp="130140czctjqs" />
                  <span className={`mt-2 flex items-center gap-1.5 text-[13px] ${done ? 'text-ink' : 'text-muted'}`} data-id="element-6080" data-mp="1cw447danb8b3">
                    {i < current && <CheckIcon className="h-3.5 w-3.5 shrink-0 text-clay" strokeWidth={2} aria-hidden="true" data-id="element-6081" data-mp="17yb0cqwteqdz" />}
                    <span className="truncate" data-id="element-6082" data-mp="3eo93xaydz9wd">{s.label}</span>
                    {i < current && <span className="sr-only" data-id="element-6083" data-mp="39po2j87eek6c"> (gedaan)</span>}
                  </span>
                </li>;
        })}
          </ol>
          <OrderTracking order={order} postcode={account.customer.address.postcode} data-id="element-6084" data-mp="1p8y2m92roqn7" />
        </>}

      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-line px-5 py-3 sm:px-7" data-id="element-6085" data-mp="1u7j2u8i0rabn">
        <Link to={orderHref(order.id)} className="inline-flex items-center gap-1.5 py-1 text-[14px] text-ink underline-offset-4 hover:underline" data-id="element-6086" data-mp="1kfddl6j5j705">
          Bekijk bestelling
          <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-6087" data-mp="16igob9iolxld" />
        </Link>
        {moreOpen > 0 && <Link to="/account/bestellingen" className="py-1 text-[13px] text-muted underline-offset-4 hover:text-ink hover:underline" data-id="element-6088" data-mp="wig0pbds4vbd">
            Nog {plural(moreOpen, 'bestelling', 'bestellingen')} onderweg
          </Link>}
      </div>
    </section>;
}